import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AlertTriangle, ArrowRight, KeyRound, Lock, Phone, X } from "lucide-react";
import { Button } from "@/components/atoms/Button";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import {
  whatsappNumberReplacementService,
  type ReplaceNumberStatusResponse,
} from "@/services/whatsappNumberReplacementService";
import { isAlreadyInProgress, isTransportFailure, replacementErrorMessage } from "./replacementErrors";

/**
 * Starts a WhatsApp number replacement for one already-configured channel.
 *
 * ## Why this is not an "edit the number" form
 *
 * Submitting begins a durable, multi-system cutover in Atlas: Meta-side
 * verification, a routing switch on bridge-whatsapp-gateway, a mapping change
 * on bridge-chat-gateway, then the channel record catching up. The dialog is
 * therefore two deliberate steps — details, then an explicit confirmation of
 * what is about to happen — and the outcome is owned by Atlas, not by whether
 * this POST returned 2xx.
 *
 * ## Same-WABA only (Phase 1)
 *
 * Atlas rejects a different WhatsApp Business Account with
 * `CROSS_WABA_REPLACEMENT_NOT_ENABLED`. Rather than offer a field that can
 * only be filled in one valid way, the WABA is shown read-only and submitted
 * as the channel's current value, so the UI cannot suggest a capability the
 * backend does not have.
 *
 * ## Credential handling
 *
 * `newAccessToken` is required by Atlas's `ReplaceNumberRequest` (`@NotBlank`)
 * — it is what proves the new number belongs to the caller. It lives in this
 * component's state and nowhere else: it is cleared on unmount and on success,
 * never written to storage, never logged, never put in a URL, and never
 * rendered back after submission. This mirrors how the existing WhatsApp
 * credential fields on this page behave.
 */

export interface WhatsappNumberReplacementModalProps {
  channelId: string;
  /** Legal entity or branch this channel belongs to — shown for orientation. */
  entityName: string;
  currentPhone: string;
  currentPhoneNumberId: string;
  /** The channel's current WABA. Phase 1 replaces within this account only. */
  currentWabaId: string;
  onClose: () => void;
  /** Called with Atlas's response once a replacement record exists. */
  onStarted: (op: ReplaceNumberStatusResponse) => void;
  /**
   * Called when Atlas rejects the start because a replacement already holds
   * the channel lock — the caller re-reads the status endpoint and shows it.
   */
  onAlreadyInProgress: () => void;
}

const E164 = /^\+[1-9]\d{1,14}$/;
const PHONE_NUMBER_ID = /^\d{6,25}$/;

/** Stable per-attempt key so a retry resumes rather than starting a second operation. */
function newIdempotencyKey(): string {
  const uuid =
    typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
      ? crypto.randomUUID()
      : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `atlas-ui:${uuid}`;
}

const FieldLabel: React.FC<{ htmlFor: string; children: React.ReactNode; required?: boolean }> = ({
  htmlFor,
  children,
  required,
}) => (
  <label
    htmlFor={htmlFor}
    className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider flex items-center gap-1"
  >
    {children}
    {required && <span className="text-red-500 normal-case">*</span>}
  </label>
);

export const WhatsappNumberReplacementModal: React.FC<WhatsappNumberReplacementModalProps> = ({
  channelId,
  entityName,
  currentPhone,
  currentPhoneNumberId,
  currentWabaId,
  onClose,
  onStarted,
  onAlreadyInProgress,
}) => {
  const [step, setStep] = useState<"details" | "confirm">("details");
  const [newPhone, setNewPhone] = useState("");
  const [newPhoneNumberId, setNewPhoneNumberId] = useState("");
  const [newAccessToken, setNewAccessToken] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [touched, setTouched] = useState(false);

  /**
   * Belt and braces against a double submit. `submitting` alone is not enough:
   * two clicks dispatched in the same React batch both read the pre-update
   * value, so the ref — which updates synchronously — is what actually stops
   * the second request.
   */
  const submittingRef = useRef(false);
  /**
   * One key for the whole dialog session. A retry after a timeout or a
   * transient 5xx therefore RESUMES the same Atlas operation from its
   * persisted stage instead of creating a second one.
   */
  const idempotencyKeyRef = useRef<string>(newIdempotencyKey());
  const headingRef = useRef<HTMLHeadingElement>(null);
  const firstFieldRef = useRef<HTMLInputElement>(null);

  const { onOverlayClick } = useModalDismiss(!submitting, onClose);

  // Focus lands inside the dialog on open, and moves to the confirmation
  // heading on step change so a screen reader announces the new context.
  useEffect(() => {
    if (step === "details") firstFieldRef.current?.focus();
    else headingRef.current?.focus();
  }, [step]);

  // Defence in depth on the credential: even though React drops the state on
  // unmount, blank it explicitly so it cannot survive in a retained closure.
  useEffect(
    () => () => {
      setNewAccessToken("");
    },
    [],
  );

  const trimmedPhone = newPhone.trim();
  const trimmedPhoneNumberId = newPhoneNumberId.trim();

  const phoneError = !trimmedPhone
    ? "Enter the new WhatsApp number."
    : !E164.test(trimmedPhone)
      ? "Use E.164 format, e.g. +441342453254."
      : trimmedPhone === currentPhone.trim()
        ? "This is the number the channel already uses."
        : null;

  const phoneNumberIdError = !trimmedPhoneNumberId
    ? "Enter the Meta phone number ID for the new number."
    : !PHONE_NUMBER_ID.test(trimmedPhoneNumberId)
      ? "The Meta phone number ID is numeric (15–20 digits)."
      : trimmedPhoneNumberId === currentPhoneNumberId.trim()
        ? "This is the phone number ID the channel already uses."
        : null;

  const tokenError = !newAccessToken.trim() ? "Enter an access token that can read this business account." : null;

  const formValid = !phoneError && !phoneNumberIdError && !tokenError;

  const handleContinue = (e: React.FormEvent) => {
    e.preventDefault();
    setTouched(true);
    setError(null);
    if (!formValid) return;
    setStep("confirm");
  };

  const handleSubmit = async () => {
    if (submittingRef.current) return;
    submittingRef.current = true;
    setSubmitting(true);
    setError(null);
    try {
      const op = await whatsappNumberReplacementService.start(channelId, {
        newPhone: trimmedPhone,
        newPhoneNumberId: trimmedPhoneNumberId,
        newWabaId: currentWabaId.trim(),
        newAccessToken,
        idempotencyKey: idempotencyKeyRef.current,
      });
      setNewAccessToken("");
      onStarted(op);
    } catch (err) {
      if (isAlreadyInProgress(err)) {
        // Another session won the race, or an earlier attempt is still
        // holding the lock. Atlas is authoritative — hand off to the status
        // panel rather than showing this as a form error.
        setNewAccessToken("");
        onAlreadyInProgress();
        return;
      }
      if (isTransportFailure(err)) {
        // The request may have been created. Do not report failure and do not
        // let the operator fire a fresh attempt: send them to the live status.
        setError(
          "BridgeAI could not be reached, so the outcome is unknown. The change may still have started — " +
            "close this dialog to see the channel's current replacement status before trying again.",
        );
        return;
      }
      setError(replacementErrorMessage(err, "The number change could not be started. Please try again."));
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const dialog = (
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-bridge-fg/20 backdrop-blur-sm"
      onClick={onOverlayClick}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="wa-replace-title"
        className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border">
          <div>
            <h3 id="wa-replace-title" ref={headingRef} tabIndex={-1} className="text-lg font-bold text-bridge-fg outline-none">
              Change WhatsApp number
            </h3>
            <p className="text-xs text-bridge-muted">{entityName}</p>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            aria-label="Close"
            className="p-1 text-bridge-muted hover:text-bridge-fg transition-colors disabled:opacity-40"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {step === "details" ? (
          <form onSubmit={handleContinue} className="p-6 space-y-5">
            <div className="rounded-xl border border-bridge-border bg-slate-50 p-4 space-y-1">
              <div className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider">
                Current WhatsApp number
              </div>
              <div className="text-sm font-bold text-bridge-fg">{currentPhone || "—"}</div>
              <div className="text-[11px] text-bridge-muted font-mono">Phone number ID {currentPhoneNumberId || "—"}</div>
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="wa-replace-new-phone" required>
                New WhatsApp number
              </FieldLabel>
              <div className="relative">
                <Phone className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-bridge-muted/60 pointer-events-none" />
                <input
                  id="wa-replace-new-phone"
                  ref={firstFieldRef}
                  type="tel"
                  autoComplete="off"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  placeholder="+441342453254"
                  aria-invalid={touched && !!phoneError}
                  aria-describedby={touched && phoneError ? "wa-replace-new-phone-err" : undefined}
                  className="h-11 w-full pl-10 pr-3.5 bg-white border border-bridge-border rounded-lg text-sm text-bridge-fg focus:border-bridge-accent focus:ring-2 focus:ring-bridge-accent/20 outline-none"
                />
              </div>
              {touched && phoneError && (
                <p id="wa-replace-new-phone-err" className="text-[11px] text-red-600">
                  {phoneError}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="wa-replace-new-pnid" required>
                New Meta phone number ID
              </FieldLabel>
              <input
                id="wa-replace-new-pnid"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                value={newPhoneNumberId}
                onChange={(e) => setNewPhoneNumberId(e.target.value)}
                placeholder="123456789012345"
                aria-invalid={touched && !!phoneNumberIdError}
                aria-describedby={touched && phoneNumberIdError ? "wa-replace-new-pnid-err" : undefined}
                className="h-11 w-full px-3.5 bg-white border border-bridge-border rounded-lg text-sm font-mono text-bridge-fg focus:border-bridge-accent focus:ring-2 focus:ring-bridge-accent/20 outline-none"
              />
              {touched && phoneNumberIdError && (
                <p id="wa-replace-new-pnid-err" className="text-[11px] text-red-600">
                  {phoneNumberIdError}
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <FieldLabel htmlFor="wa-replace-token" required>
                Meta access token for the new number
              </FieldLabel>
              <div className="relative">
                <KeyRound className="w-4 h-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-bridge-muted/60 pointer-events-none" />
                <input
                  id="wa-replace-token"
                  type="password"
                  autoComplete="new-password"
                  value={newAccessToken}
                  onChange={(e) => setNewAccessToken(e.target.value)}
                  placeholder="EAAxxxxxxxxxxxx…"
                  aria-invalid={touched && !!tokenError}
                  aria-describedby="wa-replace-token-hint"
                  className="h-11 w-full pl-10 pr-3.5 bg-white border border-bridge-border rounded-lg text-xs font-mono text-bridge-fg focus:border-bridge-accent focus:ring-2 focus:ring-bridge-accent/20 outline-none"
                />
              </div>
              <p id="wa-replace-token-hint" className="text-[11px] text-bridge-muted">
                Used once to verify the number with Meta. It is not shown again after you submit.
              </p>
              {touched && tokenError && <p className="text-[11px] text-red-600">{tokenError}</p>}
            </div>

            <div className="rounded-xl border border-bridge-border bg-slate-50 p-4 space-y-1.5">
              <FieldLabel htmlFor="wa-replace-waba">WhatsApp Business Account</FieldLabel>
              <div
                id="wa-replace-waba"
                className="h-11 w-full inline-flex items-center justify-between gap-2 px-3.5 bg-slate-100 border border-bridge-border rounded-lg text-xs font-mono text-bridge-fg"
              >
                <span className="truncate">{currentWabaId || "—"}</span>
                <Lock className="w-4 h-4 text-bridge-muted shrink-0" aria-hidden="true" />
              </div>
              <p className="text-[11px] text-bridge-muted">
                The new number must already sit in this same WhatsApp Business Account. Moving a channel to a
                different business account is not supported.
              </p>
            </div>

            {error && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3 pt-1">
              <Button type="button" variant="secondary" className="flex-1 rounded-xl" onClick={onClose}>
                Cancel
              </Button>
              <Button type="submit" variant="primary" className="flex-1 rounded-xl">
                Continue
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </form>
        ) : (
          <div className="p-6 space-y-5">
            <div className="rounded-xl border border-amber-200 bg-amber-50 p-4 flex gap-3">
              <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" aria-hidden="true" />
              <div className="text-sm text-amber-900 space-y-2">
                <p className="font-bold">This moves live customer messaging.</p>
                <p className="text-xs leading-relaxed">
                  BridgeAI will verify the new number with Meta, then switch this channel's WhatsApp traffic to it.
                  The channel and its Chatwoot inbox stay the same, so existing conversations, contacts, agents and
                  bot configuration are preserved. Messages sent to the old number after the switch will not arrive.
                </p>
                <p className="text-xs leading-relaxed">
                  The change runs in BridgeAI and continues even if you close this page. No other number change can
                  be started on this channel until it finishes.
                </p>
              </div>
            </div>

            <div className="rounded-xl border border-bridge-border overflow-hidden">
              <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-3 p-4">
                <div className="min-w-0">
                  <div className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider">From</div>
                  <div className="text-sm font-bold text-bridge-fg truncate">{currentPhone || "—"}</div>
                  <div className="text-[10px] text-bridge-muted font-mono truncate">{currentPhoneNumberId || "—"}</div>
                </div>
                <ArrowRight className="w-5 h-5 text-bridge-muted" aria-hidden="true" />
                <div className="min-w-0">
                  <div className="text-[10px] font-bold text-bridge-muted uppercase tracking-wider">To</div>
                  <div className="text-sm font-bold text-bridge-fg truncate">{trimmedPhone}</div>
                  <div className="text-[10px] text-bridge-muted font-mono truncate">{trimmedPhoneNumberId}</div>
                </div>
              </div>
            </div>

            {error && (
              <div role="alert" className="rounded-xl border border-red-200 bg-red-50 p-3 text-xs text-red-700">
                {error}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                type="button"
                variant="secondary"
                className="flex-1 rounded-xl"
                disabled={submitting}
                onClick={() => {
                  setError(null);
                  setStep("details");
                }}
              >
                Back
              </Button>
              <Button
                type="button"
                variant="primary"
                className="flex-1 rounded-xl"
                loading={submitting}
                disabled={submitting}
                onClick={handleSubmit}
              >
                {submitting ? "Starting…" : "Change number"}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );

  return createPortal(dialog, document.body);
};
